import { useEffect, useRef } from 'react';
import { Mic, Send, ArrowRight, Check, RotateCcw, AlertTriangle } from 'lucide-react';
import { useStore } from '../store';
import { VTile } from '../components/VMark';
import { Markdown } from '../components/Markdown';
import type { ChatMessage } from '../types';

// The conversational assessment surface: Avery interviews the participant over three stages,
// driven entirely by the store's existing chat plumbing (sendMessage / streamTurn / quickCommand
// / voice). Rendered by Assessment() when the session mode is `conversational`.

// Stage names mirror the backend STAGE_TITLES (state-machine.ts). The interview runs 1→3.
const STAGE_TITLES = ['Workflow walkthrough', 'Leverage snapshot', 'Coaching & roadmap'];

const QUICK_COMMANDS: { cmd: string; label: string }[] = [
  { cmd: 'skip', label: 'Skip' },
  { cmd: 'park', label: 'Park for later' },
  { cmd: 'example', label: 'Give me an example' },
  { cmd: 'rephrase', label: 'Rephrase' },
  { cmd: 'go_deeper', label: 'Go deeper' },
  { cmd: 'status', label: 'Status' },
];

export function Chat() {
  const messages = useStore((s) => s.messages);
  const typing = useStore((s) => s.typing);
  const completed = useStore((s) => s.completed);
  const topicsDone = useStore((s) => s.topicsDone);
  const openConversation = useStore((s) => s.openConversation);

  // Greet on first mount (self-guards if a transcript was already restored on resume).
  useEffect(() => openConversation(), [openConversation]);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, typing]);

  // topicsDone counts completed stages (0–3). Current stage is the next one in flight.
  const stageIdx = Math.min(topicsDone, STAGE_TITLES.length - 1);
  const stageNo = completed ? STAGE_TITLES.length : stageIdx + 1;
  const pct = completed ? 100 : Math.round((Math.min(topicsDone, STAGE_TITLES.length) / STAGE_TITLES.length) * 100);

  return (
    <div style={{ background: '#F4F4F6', minHeight: 'calc(100vh - 60px)' }}>
      <div
        style={{
          maxWidth: 900,
          margin: '0 auto',
          background: '#fff',
          borderLeft: '1px solid #E2E2E6',
          borderRight: '1px solid #E2E2E6',
          height: 'calc(100vh - 60px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ProgressHeader stageNo={stageNo} title={STAGE_TITLES[stageIdx]} pct={pct} done={completed} />
        <div ref={scrollRef} className="fui-step-scroll" style={{ flex: 1, overflowY: 'auto', padding: '26px 30px 30px' }}>
          <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
            {messages.map((m) => (
              <Bubble key={m.id} m={m} />
            ))}
            {typing && <TypingIndicator />}
          </div>
        </div>
        <Composer />
      </div>
    </div>
  );
}

function ProgressHeader({ stageNo, title, pct, done }: { stageNo: number; title: string; pct: number; done: boolean }) {
  return (
    <div style={{ flex: 'none', padding: '16px 30px 14px', borderBottom: '1px solid #EFEFF2', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
          <VTile size={36} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#000' }}>Avery · your guide</div>
            <div style={{ fontSize: 12, color: '#75757F', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Stage {stageNo} of 3 · {title}
            </div>
          </div>
        </div>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#00A800' }}>{done ? 'Complete' : `${pct}%`}</span>
      </div>
      <div role="progressbar" aria-label="Assessment stages completed" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} style={{ marginTop: 12, height: 6, borderRadius: 999, background: '#EFEFF2', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: '#00D700', borderRadius: 999, transition: 'width .6s' }} />
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        {STAGE_TITLES.map((t, i) => {
          const active = i + 1 === stageNo && !done;
          const complete = done || i + 1 < stageNo;
          return (
            <div
              key={t}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                fontSize: 11,
                fontWeight: 700,
                padding: '5px 8px',
                borderRadius: 999,
                border: `1px solid ${active ? '#00D700' : complete ? '#C8E6C8' : '#E2E2E6'}`,
                background: active ? '#F6FCF6' : complete ? '#F6FCF6' : '#fff',
                color: active || complete ? '#00A800' : '#9A9AA4',
              }}
            >
              {complete && <Check size={12} strokeWidth={3} />}
              {t}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Bubble({ m }: { m: ChatMessage }) {
  if (m.role === 'user') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ maxWidth: '82%', background: '#000', color: '#fff', fontSize: 14.5, lineHeight: 1.55, padding: '12px 15px', borderRadius: 12, borderBottomRightRadius: 3 }}>
          {m.text}
        </div>
      </div>
    );
  }

  if (m.type === 'reflection') {
    return (
      <AveryRow>
        <div style={{ border: '1px solid #C8E6C8', borderLeft: '3px solid #00D700', borderRadius: 12, background: '#F6FCF6', padding: '14px 16px', maxWidth: '88%' }}>
          <Eyebrow>What I heard so far</Eyebrow>
          <div style={{ fontSize: 14.5, lineHeight: 1.55, color: '#1A1A22', marginTop: 4 }}><Markdown>{m.text}</Markdown></div>
          {m.insight && (
            <>
              <div style={{ borderTop: '1px solid #DBEEDB', margin: '12px 0 10px' }} />
              <Eyebrow>Early insight</Eyebrow>
              <div style={{ fontSize: 14, lineHeight: 1.55, color: '#1A1A22', marginTop: 4 }}><Markdown>{m.insight}</Markdown></div>
            </>
          )}
        </div>
      </AveryRow>
    );
  }

  if (m.type === 'assumption') {
    return (
      <AveryRow>
        <AssumptionCard m={m} />
      </AveryRow>
    );
  }

  // text / question
  return (
    <AveryRow>
      <div style={{ background: '#F4F4F6', color: '#333544', fontSize: 14.5, lineHeight: 1.55, padding: '13px 16px', borderRadius: 12, borderBottomLeftRadius: 3, maxWidth: '88%' }}>
        {m.type === 'question' && (
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#00A800', marginBottom: 6 }}>Question</div>
        )}
        <Markdown>{m.text}</Markdown>
      </div>
    </AveryRow>
  );
}

function AssumptionCard({ m }: { m: ChatMessage }) {
  const resolveAssumption = useStore((s) => s.resolveAssumption);
  return (
    <div style={{ border: '1px solid #E6D9B0', borderRadius: 12, background: '#FFFBEE', padding: '14px 16px', maxWidth: '88%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
        <AlertTriangle size={14} strokeWidth={2.2} color="#B8860B" />
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8A6D1B' }}>
          Quick check — correct me if I'm wrong
        </span>
      </div>
      <div style={{ fontSize: 14.5, lineHeight: 1.55, color: '#1A1A22' }}><Markdown>{m.text}</Markdown></div>
      {m.resolved ? (
        <div style={{ fontSize: 13, fontWeight: 600, color: '#00A800', marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {m.resolved === 'confirmed' ? <Check size={14} strokeWidth={2.6} /> : <RotateCcw size={14} strokeWidth={2.4} />}
          {m.resolved === 'confirmed' ? 'Confirmed — thanks' : 'Noted — Avery will adjust'}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 9, marginTop: 12 }}>
          <button
            onClick={() => resolveAssumption(m.id, 'confirmed')}
            style={{ fontSize: 13, fontWeight: 700, color: '#000', background: '#00D700', border: '1.5px solid #00D700', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}
          >
            Yes, that's right
          </button>
          <button
            onClick={() => resolveAssumption(m.id, 'corrected')}
            style={{ fontSize: 13, fontWeight: 600, color: '#333544', background: '#fff', border: '1.5px solid #E2E2E6', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}
          >
            Not quite
          </button>
        </div>
      )}
    </div>
  );
}

function AveryRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <VTile size={34} className="flex-none" />
      {children}
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#00A800' }}>{children}</span>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <VTile size={34} className="animate-breathe flex-none" />
      <div style={{ background: '#F4F4F6', borderRadius: 12, borderBottomLeftRadius: 3, padding: '15px 18px', display: 'flex', gap: 6 }}>
        {[0, 0.2, 0.4].map((d, i) => (
          <span key={i} className="animate-blink" style={{ width: 8, height: 8, borderRadius: '50%', background: '#9A9AA4', animationDelay: `${d}s` }} />
        ))}
      </div>
    </div>
  );
}

function Composer() {
  const composer = useStore((s) => s.composer);
  const setComposer = useStore((s) => s.setComposer);
  const sendMessage = useStore((s) => s.sendMessage);
  const quickCommand = useStore((s) => s.quickCommand);
  const toggleListening = useStore((s) => s.toggleListening);
  const listening = useStore((s) => s.listening);
  const voiceError = useStore((s) => s.voiceError);
  const typing = useStore((s) => s.typing);
  const completed = useStore((s) => s.completed);
  const setScreen = useStore((s) => s.setScreen);
  const viewSample = useStore((s) => s.viewSample);

  const disabled = completed;
  const canSend = !disabled && !typing && composer.trim().length > 0;

  // Auto-grow the textarea with content (up to maxHeight), so longer answers are visible as
  // they're typed rather than scrolling inside a single line. Runs on every composer change,
  // including programmatic resets (e.g. after send) which collapse it back to the min height.
  const taRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [composer]);

  // Once complete the session is closed server-side, so the composer is retired. Rather than
  // auto-navigating to the report (which yanks the participant out mid-flow), keep them in the
  // conversation and offer an explicit CTA — the report is already pre-loaded in the store.
  if (completed) {
    return (
      <div style={{ flex: 'none', borderTop: '1px solid #EFEFF2', background: '#fff', padding: '16px 30px 20px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 700, color: '#00A800' }}>
            <Check size={16} strokeWidth={2.6} /> Assessment complete — your report is ready.
          </div>
          <button
            onClick={() => setScreen('report')}
            style={{ flex: 'none', fontSize: 14, fontWeight: 700, color: '#000', background: '#00D700', border: '1.5px solid #00D700', borderRadius: 10, padding: '11px 20px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 }}
          >
            View your report <ArrowRight size={15} strokeWidth={2.4} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 'none', borderTop: '1px solid #EFEFF2', background: '#fff', padding: '12px 30px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
          {QUICK_COMMANDS.map((q) => (
            <button
              key={q.cmd}
              onClick={() => quickCommand(q.cmd)}
              disabled={disabled || typing}
              style={{ fontSize: 12, fontWeight: 600, color: '#333544', background: '#F4F4F6', border: '1px solid #E2E2E6', borderRadius: 999, padding: '6px 12px', cursor: disabled || typing ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
            >
              {q.label}
            </button>
          ))}
        </div>
        <button
          onClick={viewSample}
          style={{ fontSize: 12, fontWeight: 600, color: '#75757F', background: 'transparent', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, padding: 0 }}
        >
          Skip to a sample report <ArrowRight size={13} strokeWidth={2.2} />
        </button>
      </div>

      {voiceError && <div style={{ fontSize: 12, color: '#C0392B', marginBottom: 8 }}>{voiceError}</div>}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, border: '1.5px solid #E2E2E6', borderRadius: 12, padding: 10, background: disabled ? '#F9F9FB' : '#fff' }}>
        <textarea
          ref={taRef}
          value={composer}
          disabled={disabled}
          onChange={(e) => setComposer(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (canSend) sendMessage();
            }
          }}
          rows={3}
          placeholder={completed ? 'Assessment complete — opening your report…' : 'Answer in your own words — type or speak…'}
          style={{ flex: 1, border: 'none', outline: 'none', resize: 'none', fontSize: 15, lineHeight: 1.5, fontFamily: 'inherit', background: 'transparent', color: '#000', minHeight: 68, maxHeight: 160 }}
        />
        <button
          onClick={toggleListening}
          disabled={disabled}
          aria-label={listening ? 'Stop dictation' : 'Start dictation'}
          className={listening ? 'animate-breathe' : ''}
          style={{ flex: 'none', width: 40, height: 40, borderRadius: 8, border: '1px solid #E2E2E6', background: listening ? '#F6FCF6' : '#fff', color: listening ? '#00A800' : '#75757F', cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Mic size={18} strokeWidth={2} />
        </button>
        <button
          onClick={() => canSend && sendMessage()}
          disabled={!canSend}
          aria-label="Send"
          style={{ flex: 'none', width: 40, height: 40, borderRadius: 8, border: 'none', background: canSend ? '#00D700' : '#E2E2E6', color: canSend ? '#000' : '#9A9AA4', cursor: canSend ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Send size={17} strokeWidth={2.2} />
        </button>
      </div>
      <div style={{ fontSize: 11.5, color: '#9A9AA4', marginTop: 7 }}>Press Enter to send · Shift+Enter for a new line</div>
    </div>
  );
}
